import React from "react";

import { useHistory } from "react-router-dom";

import { openDealReview } from "manager/components/vero1/openDealReview";
import OutcomesV2 from "shared/components/vega/LumenV2/OutcomesV2";

import { useDeviceType } from "shared/hooks";
import { useVeroFlag } from "shared/utils/featureFlags";

import { VeroFlag } from "shared/utils/veroFlags";

import { useDealSummaryRedesign } from "../../useDealSummaryRedesign";

import {
  categoryGroups,
  identityVerificationCategories,
  isBackgroundCheckCategory,
} from "../constants";

import { findLumenResult, queryByAnnotation } from "../queryHelpers";

import CosignStatusBar from "./CosignStatusBar";
import { categoryProcessorMap } from "./helpers/categoryProcessors";
import { NO_SSN_LABEL } from "./helpers/outcomeHelpers";
import LumenEntryCard from "./LumenEntryCard";

const LumenSummary = ({
  lumenRun,
  dealId,
  hideOutcomes = false,
  applicants,
  isLumenStillCalculating,
  vero1CardOutcomes,
  isIdentityVerificationEnabled,
  backgroundAndCreditChecksEnabled,
  applications,
  lumenRevision,
  monthlyRentDollars,
}) => {
  const history = useHistory();
  const { isMobile } = useDeviceType();
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  // shared gate: a VERO1-client company gets the redesign with no flag
  const useDealSummaryRedesignProduct1270 = useDealSummaryRedesign();
  const enableMonthlyIncomeDisplay = useVeroFlag(
    VeroFlag.enableMonthlyIncomeDisplayProduct1486
  );

  const containerStyles = useDealSummaryRedesignProduct1270
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        alignItems: "stretch",
        gap: "12px",
        width: "100%",
        justifyItems: "stretch",
        padding: 0,
      }
    : {
        display: "flex",
        flexWrap: "wrap" as "wrap",
        justifyContent: useDealsListUiRedesign ? "space-between" : "center",
        alignItems: "stretch",
        gap: "4px",
        width: "100%",
        paddingLeft: useDealsListUiRedesign && !isMobile ? "8px" : undefined,
      };
  const cardContainerStyles = useDealSummaryRedesignProduct1270
    ? { display: "flex" }
    : {
        flex: isMobile ? "1 1 100%" : "0 0 280px",
        minWidth: isMobile ? "100%" : "280px",
        maxWidth: isMobile ? "100%" : "280px",
        minHeight: "100px",
        display: "flex",
        margin: "8px",
      };

  // Get the overall application result (not per-applicant)
  const applicationLumenResult = findLumenResult(lumenRun);

  // Generate results for each applicant
  const applicantLumenResults =
    applicants?.map((applicant) => {
      const result = findLumenResult(lumenRun, applicant.id);
      return {
        applicantId: applicant.id,
        applicantName: `${applicant.firstName} ${applicant.lastName}`,
        lumenResult: result,
      };
    }) || [];

  const filteredCategoryGroups = categoryGroups.filter(({ id }) => {
    // Filter out identity verification if not enabled
    if (identityVerificationCategories.includes(id)) {
      return isIdentityVerificationEnabled;
    }
    // Filter out background checks if not enabled
    if (isBackgroundCheckCategory(id)) {
      return backgroundAndCreditChecksEnabled;
    }
    return true;
  });

  const cosignApplicationStatus = queryByAnnotation(
    applicationLumenResult,
    "has-approved-cosign"
  );

  return (
    <div
      className="vega container"
      style={
        useDealSummaryRedesignProduct1270
          ? {
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              padding: "20px 26px 26px",
              borderRadius: "0 0 14px 14px",
              boxShadow: "0 18px 50px -22px rgba(45, 41, 67, 0.5)",
            }
          : undefined
      }
    >
      <section className="vega flex content" style={containerStyles}>
        {filteredCategoryGroups.map(({ id, name }) => {
          const processor = categoryProcessorMap[id];
          // eslint-disable-next-line prefer-const
          let { label, status, details, cosignApproved, tooltipContent } =
            processor({
              applicationLumenResult,
              applicantLumenResults,
              isLumenStillCalculating,
              lumenRun,
              applications,
              lumenRevision,
              enableMonthlyIncomeDisplay,
              monthlyRentDollars,
            });
          const reviewOutcome = vero1CardOutcomes?.[id];
          if (vero1CardOutcomes && label === NO_SSN_LABEL) {
            label = null;
            details = null;
          }
          if (reviewOutcome) {
            status = reviewOutcome.tone;
          }
          return (
            <div key={id} style={cardContainerStyles}>
              <LumenEntryCard
                title={name}
                label={label}
                status={status}
                details={details}
                reviewInForce={Boolean(vero1CardOutcomes)}
                onOpenReview={
                  reviewOutcome?.reviewCategory
                    ? () =>
                        openDealReview(
                          history,
                          dealId,
                          reviewOutcome.reviewCategory
                        )
                    : undefined
                }
                cosignApproved={cosignApproved}
                tooltipContent={tooltipContent}
                lgTone={useDealSummaryRedesignProduct1270}
              />
            </div>
          );
        })}
      </section>
      {cosignApplicationStatus && <CosignStatusBar />}
      {!hideOutcomes && (
        <OutcomesV2
          lumenRun={lumenRun}
          lgTone={useDealSummaryRedesignProduct1270}
        />
      )}
    </div>
  );
};

export default LumenSummary;
